import React, { useEffect, useMemo, useRef, useState } from 'react';
import VehicleService from '../../../services/VehicleService';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import {
  FormControl,
  TextField,
  Paper,
  IconButton
} from '@material-ui/core';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Popper,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import useStyles from '../../../styles/MissionAssignVehicle.style';
import AddIcon from '@mui/icons-material/Add';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BadgeIcon from '@mui/icons-material/Badge';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import GroupIcon from '@mui/icons-material/Group';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import MissionTransportService from '../../../services/MissionTransportService';
import Message from '../../tools/Message';
import Spacer from '../../../components/Spacer';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { formatDate } from '../../../helpers/FormateDate';
import CarPoolingConfirm from './CarPoolingConfirm';
import {
  VEHICLE_ASSIGNMENT_MODE as MODE_AFFECTATION_VEHICULE,
  buildVehicleAssignmentSelection as construireSelectionVehicule,
  getTransportIdForPayload as obtenirIdTransportPayload,
  getVehicleSelectionReference as obtenirReferenceSelectionVehicule,
  isSeparateTrip as estCourseSeparee,
  mergeVehicleAssignmentSelection as fusionnerSelectionVehicule,
} from './MissionAssignVehicleMode';

const ETAT_INITIAL_COVOITURAGE = {
  open: false,
  groupIndex: null,
  rowIndex: null,
  vehicle: null,
  action: null,
  mode: MODE_AFFECTATION_VEHICULE.CARPOOLING,
};

const ETAT_INITIAL_SUPPRESSION = {
  open: false,
  groupIndex: null,
  rowIndex: null,
  row: null,
  loading: false,
};

const STYLE_BOUTON_ACTION = {
  fontWeight: 500,
  textTransform: 'uppercase',
  width: { xs: '100%', sm: 'auto' },
};

const creerMessage = (color, messageInfo) => ({ open: true, color, messageInfo });

const normaliserTypeTransport = (transportType, missionRentId = null) => {
  const typeBrut = (transportType || '').toString().trim();
  const typeNormalise = typeBrut.toUpperCase();

  if (missionRentId) {
    return typeBrut || 'LOCATION';
  }
  if (!typeNormalise || ['FID', 'VOITURE', 'VOITURE FID'].includes(typeNormalise)) {
    return 'FID';
  }
  if (['AVION', 'BATEAU'].includes(typeNormalise)) {
    return typeNormalise;
  }
  return typeBrut;
};

const normaliserTypeTransportMinuscule = (transportType, missionRentId = null) =>
  normaliserTypeTransport(transportType, missionRentId).toLowerCase();

const estTransportLocation = (transportType, missionRentId = null) =>
  Boolean(missionRentId) ||
  normaliserTypeTransportMinuscule(transportType, missionRentId).includes('location');

const estTransportFid = (transportType, missionRentId = null) =>
  !missionRentId &&
  normaliserTypeTransportMinuscule(transportType, missionRentId).includes('fid');

const normaliserIdVehicule = (value) =>
  value === null || value === undefined || value === ''
    ? null
    : String(value);

const obtenirIdentiteVehicule = (vehicle = {}) => {
  const vehicleId = normaliserIdVehicule(vehicle?.vhc_id);
  if (!vehicleId) {
    return null;
  }

  const transportId = normaliserIdVehicule(vehicle?.transport_id);
  return transportId
    ? `transport:${transportId}:vehicle:${vehicleId}`
    : `vehicle:${vehicleId}`;
};

const isSameVehicle = (option, value) => {
  const optionIdentity = obtenirIdentiteVehicule(option);
  const valueIdentity = obtenirIdentiteVehicule(value);
  return Boolean(optionIdentity && optionIdentity === valueIdentity);
};

const obtenirLibelleVehicule = (vehicle = {}) => {
  const registration =
    vehicle?.vhc_imm ||
    vehicle?.immatriculation ||
    vehicle?.registration ||
    '';
  const type =
    vehicle?.vhc_type ||
    vehicle?.rent_type ||
    vehicle?.transport_type ||
    'Location';
  const places = vehicle?.vhc_place ?? vehicle?.place_count;
  const description =
    vehicle?.vhc_mark ||
    vehicle?.vhc_description ||
    vehicle?.driver_name ||
    vehicle?.label ||
    vehicle?.name ||
    '';
  const vehicleId = normaliserIdVehicule(vehicle?.vhc_id);
  const mainLabel =
    registration ||
    description ||
    (vehicleId ? `Véhicule ${vehicleId}` : `Véhicule ${type}`);

  return places !== null && places !== undefined
    ? `${mainLabel} (voiture ${type} - ${places} places disponibles)`
    : `${mainLabel} (voiture ${type})`;
};

const CATALOGUE_VEHICULES_VIDE = Object.freeze({
  fid: Object.freeze([]),
  location: Object.freeze([]),
});

// En édition, une sélection libre modifie la ligne existante. Seule une
// course séparée force la création d'un nouveau transport.
const resoudreIdTransportApresSelection = (ligne = {}, vehicule = {}) => {
  const mode = vehicule?.vehicle_assignment_mode || null;
  if (mode === MODE_AFFECTATION_VEHICULE.SEPARATE_TRIP) {
    return null;
  }
  if (mode === MODE_AFFECTATION_VEHICULE.CARPOOLING) {
    return vehicule?.transport_id || vehicule?.source_transport_id || null;
  }
  if (vehicule?.transport_id) {
    return vehicule.transport_id;
  }
  return ligne?.transport_id || ligne?.transport_id_old || null;
};

const obtenirIdLocationDestination = (destination = {}) => {
  const transportLocation = (Array.isArray(destination?.transports)
    ? destination.transports
    : []
  ).find((transport) =>
    transport?.mission_rent_id !== null &&
    transport?.mission_rent_id !== undefined &&
    transport?.mission_rent_id !== ''
  );

  return transportLocation?.mission_rent_id ?? destination?.mission_rent_id ?? null;
};

const construireTransportsPayload = (groupes = [], chefParDefaut = null) =>
  groupes.flatMap((groupe) =>
    (groupe.transports || []).map((ligne) => {
      const missionRentId = ligne?.mission_rent_id ?? groupe?.mission_rent_id ?? null;
      return {
        vhc_id: ligne.vhc_id || null,
        driver_id: ligne.driver_id || null,
        captain_id: ligne.captain_id || chefParDefaut || null,
        transport_id: obtenirIdTransportPayload(ligne),
        transport_id_old: ligne.transport_id_old || null,
        vhc_energy: ligne.vhc_energy || null,
        vhc_conso_100km: ligne.vhc_conso_100km || null,
        destinations: (ligne.destinations || []).map((destination) => ({
          mission_destination_id: destination?.mission_destination_id || null,
          mission_timesheet_destination_id:
            destination?.mission_timesheet_destination_id || null,
        })),
        mission_rent_id: missionRentId,
        transport_type: normaliserTypeTransport(
          ligne.transport_type || groupe.transport_type,
          missionRentId
        ),
        missionnaries: (ligne.missionnaries || []).map(
          (missionnaire) => missionnaire?.user_id || missionnaire
        ),
      };
    })
  );

const LibelleChampVehicule = ({ icon, children, required = true }) => (
  <Box
    component="label"
    sx={{
      alignItems: 'center',
      color: '#102d64',
      display: 'flex',
      fontSize: 14,
      fontWeight: 500,
      gap: 1,
      mb: 0.75,
    }}
  >
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: '#e7f5ff',
        borderRadius: '50%',
        color: '#0767cf',
        display: 'flex',
        flex: '0 0 auto',
        height: 36,
        justifyContent: 'center',
        width: 36,
      }}
    >
      {icon}
    </Box>
    <span>{children}</span>
    {required && <Box component="span" sx={{ color: '#e31b23' }}>*</Box>}
  </Box>
);

const ChampSelection = ({
  icon,
  libelle,
  libelleSaisie,
  options,
  valeur,
  onChange,
  multiple = false,
  disabled = false,
  getOptionLabel,
  isOptionEqualToValue,
}) => (
  <FormControl fullWidth>
    <LibelleChampVehicule icon={icon}>{libelle}</LibelleChampVehicule>
    <Autocomplete
      multiple={multiple}
      size="small"
      options={options}
      value={valeur}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={(event, nouvelleValeur) => onChange(nouvelleValeur)}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" size="small" label={libelleSaisie} />
      )}
    />
  </FormControl>
);

const DialogueSuppressionVehicule = ({
  dialogue,
  idsPersistes,
  classes,
  onFermer,
  onConfirmer,
}) => {
  const transportPersiste = idsPersistes.some(
    (id) => Number(id) === Number(dialogue.row?.transport_id || 0)
  );

  return (
    <Dialog
      open={dialogue.open}
      onClose={onFermer}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: classes.dialogPaperCompact }}
    >
      <DialogTitle>Confirmer la suppression</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          Voulez-vous vraiment supprimer ce véhicule ?
          {transportPersiste && <> Cette suppression sera appliquée immédiatement.</>}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer} disabled={dialogue.loading}>Annuler</Button>
        <LoadingButton
          onClick={onConfirmer}
          loading={dialogue.loading}
          color="error"
          variant="contained"
        >
          Supprimer
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const DialogueAvertissementKilometrage = ({ dialogue, classes, onFermer }) => (
  <Dialog
    open={dialogue.open}
    onClose={onFermer}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
    PaperProps={{ className: classes.dialogPaperCompact }}
  >
    <DialogTitle id="form-dialog-title">Confirmation impossible</DialogTitle>
    <DialogContent dividers className={classes.dialogContent}>
      <DialogContentText className={classes.dialogText}>
        La confirmation ne peut pas être effectuée, car les kilométrages de la mission ne sont
        pas encore saisis ou validés. Veuillez envoyer une demande de saisie et valider les
        kilométrages avant de confirmer la mission.
      </DialogContentText>
    </DialogContent>
    <DialogActions className={classes.dialogActions}>
      <Button onClick={onFermer} color="primary" autoFocus className={classes.actionButton}>
        Fermer
      </Button>
    </DialogActions>
  </Dialog>
);

const InformationsCovoiturage = ({ details, modifiable, onVoir, onModifier }) => (
  <Paper
    variant="outlined"
    style={{
      padding: 16,
      borderRadius: 12,
      background: 'linear-gradient(135deg, #eff9ff 0%, #e5f5ff 100%)',
      border: '1px solid #cbe9fb',
      width: '100%',
    }}
  >
    <Alert
      severity="info"
      icon={<InfoOutlinedIcon sx={{ fontSize: 28 }} />}
      sx={{
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
        color: '#102d64',
        mb: 1,
        p: 0,
        '& .MuiAlert-message': { width: '100%' },
      }}
      action={modifiable ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" aria-label="Voir les détails du covoiturage" onClick={onVoir}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={onModifier}>
            Modifier
          </Button>
        </Box>
      ) : null}
    >
      <Box sx={{ fontSize: 14, fontWeight: 600 }}>Cette voiture est en covoiturage</Box>
      <Box sx={{ color: '#6680a6', fontSize: 14 }}>Elle est déjà associée à d'autres missions.</Box>
    </Alert>
    {details.map((detail) => (
      <Box
        key={detail.key}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.82)',
          border: '1px solid #bfe3f9',
          borderRadius: 2,
          mb: 1.5,
          p: 1.5,
        }}
      >
        <Box sx={{ alignItems: 'center', color: '#102d64', display: 'flex', gap: 1 }}>
          <SupervisorAccountIcon fontSize="small" />
          <Box>
            <Box sx={{ fontSize: 12.5, fontWeight: 500 }}>Chef de bord déjà assigné</Box>
            <Box sx={{ fontSize: 13.5, fontWeight: 500 }}>{detail.captainName}</Box>
          </Box>
        </Box>
        {detail.destinationsSummary && (
          <Box sx={{ alignItems: 'center', color: '#1976d2', display: 'flex', fontSize: 13.5, gap: 1, mt: 1 }}>
            <PlaceIcon fontSize="small" />
            {`Destinations : ${detail.destinationsSummary}`}
          </Box>
        )}
      </Box>
    ))}
  </Paper>
);

const InformationCourseSeparee = ({ modifiable, onModifier }) => (
  <Alert
    severity="info"
    sx={{
      backgroundColor: '#eef8ff',
      border: '1px solid #cbe9fb',
      borderRadius: 2,
      color: '#102d64',
      mb: 2,
    }}
    action={modifiable ? (
      <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={onModifier}>
        Modifier
      </Button>
    ) : null}
  >
    Course différente — carburant calculé séparément
  </Alert>
);

const obtenirIdLocationGroupe = (group = {}) => {
  if (group?.mission_rent_id !== null && group?.mission_rent_id !== undefined && group?.mission_rent_id !== '') {
    return group.mission_rent_id;
  }
  const destinations = Array.isArray(group?.destinations) ? group.destinations : [];
  const destinationLocation = destinations.find(
    (destination) => obtenirIdLocationDestination(destination) !== null
  );
  return destinationLocation ? obtenirIdLocationDestination(destinationLocation) : null;
};

// Une location possède son propre groupe; les autres transports sont regroupés par type.
const fusionnerGroupesDestinations = (groups = [], fallbackTransportType = 'FID') => {
  const mergedGroups = new Map();

  groups.forEach((group = {}) => {
    const missionRentId = obtenirIdLocationGroupe(group);
    const transportType = normaliserTypeTransport(
      group?.transport_type || fallbackTransportType,
      missionRentId
    );
    const groupKey = missionRentId
      ? `rent:${missionRentId}`
      : `type:${transportType}`;
    const existingGroup = mergedGroups.get(groupKey) || {
      transport_type: transportType,
      mission_rent_id: missionRentId,
      destinations: [],
      transports: [],
    };
    const destinations = Array.isArray(group?.destinations) ? group.destinations : [];
    const transports = Array.isArray(group?.transports) ? group.transports : [];

    existingGroup.destinations.push(
      ...destinations.map((destination) => ({
        ...destination,
        mission_rent_id: obtenirIdLocationDestination(destination) ?? missionRentId,
        transport_type: normaliserTypeTransport(
          destination?.transport_type || transportType,
          obtenirIdLocationDestination(destination) ?? missionRentId
        ),
      }))
    );
    existingGroup.transports.push(...transports);
    mergedGroups.set(groupKey, existingGroup);
  });

  return Array.from(mergedGroups.values());
};

const calculerPeriodeDestinations = (destinations = []) => {
  const debuts = destinations.map((destination) => destination.date_begin).filter(Boolean);
  const fins = destinations.map((destination) => destination.date_end).filter(Boolean);

  return {
    begin: debuts.length ? debuts.reduce((a, b) => (a < b ? a : b)) : null,
    end: fins.length ? fins.reduce((a, b) => (a > b ? a : b)) : null,
  };
};

// Dialogue d'affectation des véhicules aux destinations d'une mission.
const MissionAssignVehicle = (props) => {
  const {
    openDialog,
    closeDialog,
    missionData,
    isEdit,
    selectedDestinations = [],
    groupedDestinations = [],
    onSuccessSubmit,
    mission_timesheet_user_id
  } = props;

  const classes = useStyles();
  const mission = missionData || {};

  const [chargementOuvert, setChargementOuvert] = useState(false);
  const [enregistrementEnCours, setEnregistrementEnCours] = useState(false);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [cataloguesVehicules, setCataloguesVehicules] = useState({});
  const [missionnaries, setMissionnaries] = useState([]);
  const [groupesFormulaire, setGroupesFormulaire] = useState([]);
  const [message, setMessage] = useState({
    open: false,
    color: 'green',
    messageInfo: ''
  });
  const [ancreSurvol, setAncreSurvol] = useState(null);
  const [optionSurvolee, setOptionSurvolee] = useState(null);
  const [dialogueCovoiturage, setDialogueCovoiturage] = useState(ETAT_INITIAL_COVOITURAGE);
  const [missionsCovoiturage, setMissionsCovoiturage] = useState([]);
  const [chargementCovoiturage, setChargementCovoiturage] = useState(false);
  const [dialogueAvertissement, setDialogueAvertissement] = useState({
    open: false,
    message: ''
  });
  const [dialogueSuppression, setDialogueSuppression] = useState(ETAT_INITIAL_SUPPRESSION);
  const [idsTransportsPersistes, setIdsTransportsPersistes] = useState([]);
  const sequenceChargement = useRef(0);
  const { open, color, messageInfo } = message;

  const groupesDestinations = useMemo(() => {
    let groupesSources = [];
    if (Array.isArray(groupedDestinations) && groupedDestinations.length) {
      groupesSources = groupedDestinations;
    } else if (Array.isArray(selectedDestinations) && selectedDestinations.length) {
      if (selectedDestinations.some((item) => Array.isArray(item?.destinations))) {
        groupesSources = selectedDestinations;
      } else {
        groupesSources = selectedDestinations.map((destination) => ({
          transport_type: destination?.transport_type || mission?.transport_type || 'FID',
          mission_rent_id: obtenirIdLocationDestination(destination),
          destinations: [destination]
        }));
      }
    }
    return fusionnerGroupesDestinations(
      groupesSources,
      mission?.transport_type || 'FID'
    );
  }, [groupedDestinations, selectedDestinations, mission?.transport_type]);

  const obtenirVehiculesParType = (
    groupIndex,
    transportType,
    missionRentId = null
  ) => {
    const catalogue = cataloguesVehicules[groupIndex] || CATALOGUE_VEHICULES_VIDE;
    return estTransportLocation(transportType, missionRentId)
      ? catalogue.location
      : catalogue.fid;
  };

  const inclureVehiculeSelectionne = (vehicules = [], ligne = {}) => {
    if (!ligne?.vhc_id) {
      return vehicules;
    }

    const reference = obtenirReferenceSelectionVehicule(ligne);
    const indexExact = vehicules.findIndex((vehicule) =>
      isSameVehicle(vehicule, reference)
    );
    if (indexExact >= 0) {
      return vehicules;
    }

    const idVehicule = normaliserIdVehicule(ligne.vhc_id);
    const indexPhysique = vehicules.findIndex(
      (vehicule) => normaliserIdVehicule(vehicule?.vhc_id) === idVehicule
    );
    if (indexPhysique >= 0) {
      return vehicules;
    }
    const optionSelectionnee = {
      ...ligne,
      transport_id: reference.transport_id,
      category: ligne.category || 'En mission',
    };
    return [optionSelectionnee, ...vehicules];
  };

  const trouverOptionVehicule = (vehicles = [], vehicleRef = {}) => {
    const exactMatch = vehicles.find((vehicle) =>
      isSameVehicle(vehicle, vehicleRef)
    );
    if (exactMatch) {
      return exactMatch;
    }

    const targetVehicleId = normaliserIdVehicule(vehicleRef?.vhc_id);
    if (!targetVehicleId) {
      return null;
    }

    return (
      vehicles.find(
        (vehicle) =>
          normaliserIdVehicule(vehicle?.vhc_id) === targetVehicleId
      ) || null
    );
  };

  const fusionnerOptionsMissionnaires = (...lists) => {
    const unique = new Map();
    lists.flat().forEach((person) => {
      if (!person?.name && !person?.user_id) {
        return;
      }
      const key =
        person?.user_id !== undefined &&
        person?.user_id !== null &&
        person?.user_id !== ''
          ? `id:${person.user_id}`
          : `name:${person.name}`;
      if (!unique.has(key)) {
        unique.set(key, person);
      }
    });
    return Array.from(unique.values());
  };

  const obtenirIdChefMission = (options = missionnaries) => {
    const baseOptions = Array.isArray(options) ? options : [];
    const optionIds = new Set(
      baseOptions.map((option) => Number(option?.user_id)).filter(Boolean)
    );
    const fallbackCaptainId = Number(
      mission_timesheet_user_id || mission?.mission_timesheet_user_id || 0
    );
    if (fallbackCaptainId && optionIds.has(fallbackCaptainId)) {
      return fallbackCaptainId;
    }
    return baseOptions.find((option) => option?.user_id)?.user_id || fallbackCaptainId || 0;
  };
  
  const creerLigneTransport = (// Base template for a transport row shown in the form.
    captainId = 0,
    missionnariesList = [],
    destinations = [],
    transportType = mission?.transport_type || 'FID',
    missionRentId = null
  ) => ({
    transport_type: normaliserTypeTransport(transportType, missionRentId),
    vhc_id: 0,
    driver: null,
    vhc_conso_100km: 0,
    vhc_energy: '',
    captain_id: captainId,
    missionnaries: missionnariesList,
    driver_id: null,
    transport_id: null,
    mission_rent_id: missionRentId,
    transport_id_old: null,
    source_transport_id: null,
    vehicle_assignment_mode: null,
    destinations: destinations || []
  });

  // Une location peut être portée par le groupe ou par une de ses destinations.
  const obtenirIdLocationDuGroupe = (group = {}) => obtenirIdLocationGroupe(group);

  const convertirIdsMissionnaires = (ids = [], options = []) => {
    if (!Array.isArray(ids) || !ids.length) {
      return [];
    }
    const idSet = new Set(ids.map((id) => Number(id)));
    return options.filter((option) => idSet.has(Number(option?.user_id)));
  };

  const ajouterDestinationUnique = (list = [], destination = {}, transportType = null) =>
    dedoublonnerDestinations([
      ...list,
      {
        ...destination,
        transport_type:
          destination.transport_type || transportType || null
      }
    ]);

  // regrouper les transports par groupe de destinations et type de transport
 const construireLignesTransportGroupe = (
  group = {},
  missionnaries = [],
  defaultCaptainId = 0,
  baseRows = []
) => {
  const destinations = group?.destinations ?? [];
  const missionRentId = obtenirIdLocationDuGroupe(group);
  const transportMap = new Map();

  const resolveTransportType = (transport, row) => {
    const resolvedMissionRentId =
      transport?.mission_rent_id ??
      row?.mission_rent_id ??
      missionRentId;
    return normaliserTypeTransport(
      transport?.transport_type ||
        row?.transport_type ||
        group?.transport_type ||
        'FID',
      resolvedMissionRentId
    );
  };

  const resolveMissionRentId = (transport, row) =>
    transport?.mission_rent_id ??
    row?.mission_rent_id ??
    missionRentId;

  const getTransportKey = (transport) =>
    transport?.transport_id ??
    `${transport?.vhc_id || 'vhc'}-${transport?.transport_type || group?.transport_type || 'FID'}`;

  // ① Construire les lignes à partir des destinations
  destinations.forEach((destination) => {
    (destination?.transports ?? []).forEach((transport) => {
      const key = getTransportKey(transport);

      const existingRow =
        transportMap.get(key) ||
        creerLigneTransport(
          transport?.captain_id ?? defaultCaptainId,
          transport?.missionnaries ?? missionnaries,
          [],
          resolveTransportType(transport),
          resolveMissionRentId(transport)
        );

      transportMap.set(key, {
        ...existingRow,
        ...transport,
        transport_type: resolveTransportType(transport, existingRow),
        mission_rent_id: resolveMissionRentId(transport, existingRow),
        destinations: ajouterDestinationUnique(
          existingRow.destinations,
          destination,
          resolveTransportType(transport)
        )
      });
    });
  });

  // ② Résultat principal
  if (transportMap.size) {
    return [...transportMap.values()].map((row) => ({
      ...row,
      captain_id: row.captain_id || defaultCaptainId,
      missionnaries: Array.isArray(row.missionnaries)
        ? row.missionnaries
        : missionnaries
    }));
  }

  // ③ Repli : lignes fournies ou transports du groupe.
  const fallbackRows =
    baseRows?.length ? baseRows : group?.transports ?? [];

  if (fallbackRows.length) {
    return fallbackRows.map((row) => ({
      ...creerLigneTransport(
        row.captain_id ?? defaultCaptainId,
        row.missionnaries ?? missionnaries,
        row.destinations ?? destinations,
        row.transport_type || group?.transport_type || 'FID',
        row?.mission_rent_id ?? missionRentId
      ),
      ...row
    }));
  }

  // ④ Dernier repli : une ligne vide conserve le groupe affichable.
  return [
    creerLigneTransport(
      defaultCaptainId,
      missionnaries,
      destinations,
      group?.transport_type || 'FID',
      missionRentId
    )
  ];
};

  // Assembler les lignes de transport par groupe de destinations.
  const construireGroupesFormulaire = (
    groups = [],
    missionnariesList = [],
    captainId = 0,
    baseRows = []
  ) => {
    const normalizedGroups = Array.isArray(groups) && groups.length
        ? groups
        : [
            {
              transport_type: mission?.transport_type || 'FID',
              destinations: []
            }
          ];

    return normalizedGroups.map((group) => {
      const groupDestinations = group?.destinations || [];
      const groupMissionRentId = obtenirIdLocationDuGroupe(group);
      const groupTransportType = normaliserTypeTransport(
        group?.transport_type || 'FID',
        groupMissionRentId
      );
      // Une ligne backend ne doit alimenter que son groupe type/location.
      const groupBaseRows = (baseRows || []).filter((row) => {
        const rowMissionRentId = row?.mission_rent_id ?? null;
        if (groupMissionRentId || rowMissionRentId) {
          return Number(rowMissionRentId) === Number(groupMissionRentId);
        }
        return normaliserTypeTransport(row?.transport_type || 'FID') ===
          groupTransportType;
      });
      const transports = construireLignesTransportGroupe(
        { ...group, destinations: groupDestinations },
        missionnariesList,
        captainId,
        groupBaseRows
      );

      return {
        transport_type: groupTransportType,
        mission_rent_id: groupMissionRentId,
        destinations: groupDestinations,
        transports
      };
    });
  };

  const appliquerChefParDefaut = (rowsByGroup = [], fallbackCaptainId = 0) =>
    rowsByGroup.map((group) => ({
      ...group,
      transports: (group.transports || []).map((row) => ({
        ...row,
        captain_id: row.captain_id ? row.captain_id : fallbackCaptainId
      }))
    }));

  // Récupère les missionnaires déjà affectés dans tout le formulaire.
  const obtenirIdsMissionnairesSelectionnes = (rowsByGroup = []) =>
    rowsByGroup.flatMap((group) =>
      (group.transports || []).flatMap((row) =>
        (row.missionnaries || []).map((m) => m.user_id)
      )
    );

  // Aplatit les destinations de toutes les lignes de transport.
  const obtenirDestinationsDesLignes = (rowsByGroup = []) => {
    const collected = [];
    rowsByGroup.forEach((group) => {
      (group.transports || []).forEach((row) => {
        if (Array.isArray(row.destinations)) {
          row.destinations.forEach((dest) => {
            collected.push({
              ...dest,
              transport_type: dest.transport_type || group.transport_type || null
            });
          });
        }
      });
    });
    return collected;
  };

  const obtenirCleDestination = (dest = {}) => {
    const missionDestinationId = dest?.mission_destination_id;
    if (
      missionDestinationId !== undefined &&
      missionDestinationId !== null &&
      missionDestinationId !== ''
    ) {
      return `md:${missionDestinationId}`;
    }
    const missionTimesheetDestinationId = dest?.mission_timesheet_destination_id;
    if (
      missionTimesheetDestinationId !== undefined &&
      missionTimesheetDestinationId !== null &&
      missionTimesheetDestinationId !== ''
    ) {
      return `ts:${missionTimesheetDestinationId}`;
    }
    const start = dest?.date_begin || dest?.date_debut || '';
    const end = dest?.date_end || dest?.date_fin || '';
    return `dt:${start}-${end}`;
  };

  // Déduplique par identifiant métier, puis par plage de dates en dernier recours.
  const dedoublonnerDestinations = (list = []) => {
    const seen = new Set();
    const unique = [];
    list.forEach((dest) => {
      const key = obtenirCleDestination(dest);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      unique.push(dest);
    });
    return unique;
  };

  const obtenirDestinationsNonAffectees = (group = {}, groupIndex = -1) => {
    const groupDestinations = Array.isArray(group?.destinations) && group.destinations.length
      ? group.destinations
      : Array.isArray(groupesDestinations?.[groupIndex]?.destinations)
      ? groupesDestinations[groupIndex].destinations
      : [];

    const requiredDestinations = dedoublonnerDestinations(groupDestinations);
    const requiredKeys = new Set(requiredDestinations.map((dest) => obtenirCleDestination(dest)).filter(Boolean));

    const assignedKeys = new Set();
    (group?.transports || []).forEach((row) => {
      const hasVehicleAssigned = Boolean(row?.vhc_id || row?.transport_id);
      if (!hasVehicleAssigned) {
        return;
      }
      (row?.destinations || []).forEach((dest) => {
        const key = obtenirCleDestination(dest);
        if (requiredKeys.has(key)) {
          assignedKeys.add(key);
        }
      });
    });

    return requiredDestinations.filter((dest) => !assignedKeys.has(obtenirCleDestination(dest)));
  };

  const ajoutVehiculeDesactive = (group = {}, groupIndex = -1) => {
    const unassignedDestinations = obtenirDestinationsNonAffectees(group, groupIndex);
    return unassignedDestinations.length === 0;
  };

  const formaterLibelleDestination = (destination = {}) => {
    if (!destination) {
      return '';
    }
    const primary =
      destination.lieu_destination ||
      destination.planning_destination ||
      destination.lieu ||
      '';
    if (primary) {
      return primary;
    }
    const region = destination.region?.nom_region || '';
    const communeList = Array.isArray(destination.commune)
      ? destination.commune
      : destination.commune
      ? [destination.commune]
      : [];
    const districtList = Array.isArray(destination.district)
      ? destination.district
      : destination.district
      ? [destination.district]
      : [];
    const fokontanyList = Array.isArray(destination.fokontany)
      ? destination.fokontany
      : destination.fokontany
      ? [destination.fokontany]
      : [];
    const commune = communeList.map((c) => c.nom_commune).filter(Boolean).join(', ');
    const district = districtList.map((d) => d.nom_district).filter(Boolean).join(', ');
    const fokontany = fokontanyList.map((f) => f.nom_fokontany).filter(Boolean).join(', ');
    const parts = [region, district, commune, fokontany].filter(Boolean);
    return parts.join(' / ');
  };

  const formaterDatesDestination = (destination = {}) => {
    if (!destination) {
      return '';
    }
    const start = destination.date_debut || destination.date_begin;
    const end = destination.date_fin || destination.date_end;
    if (!start && !end) {
      return '';
    }
    if (start && end && start !== end) {
      return `${formatDate(start)} au ${formatDate(end)}`;
    }
    return formatDate(start || end);
  };

  const fusionnerGroupesVehicules = (target, source) => {
    if (!source) {
      return;
    }
    const avalaibles = Array.isArray(source.avalaibles) ? source.avalaibles : [];
    const compatibles = Array.isArray(source.compatibles) ? source.compatibles : [];
    const otherAxes = Array.isArray(source.otherAxes) ? source.otherAxes : [];
    target.avalaibles.push(...avalaibles);
    target.compatibles.push(...compatibles);
    target.otherAxes.push(...otherAxes);
  };

  const normaliserGroupesVehiculesParType = (vehiclesList) => {
    const normalized = {
      fid: { avalaibles: [], compatibles: [], otherAxes: [] },
      location: { avalaibles: [], compatibles: [], otherAxes: [] }
    };

    if (Array.isArray(vehiclesList)) {
      fusionnerGroupesVehicules(normalized.fid, { avalaibles: vehiclesList });
      return normalized;
    }

    Object.entries(vehiclesList || {}).forEach(([key, value]) => {
      const keyNorm = normaliserTypeTransportMinuscule(key);
      const bucket = keyNorm.includes('location') ? 'location' : 'fid';
      fusionnerGroupesVehicules(normalized[bucket], value);
    });

    return normalized;
  };

  const categoriserVehicules = (vehiclesList = {}) => {
    const avalaibles = Array.isArray(vehiclesList?.avalaibles)
      ? vehiclesList.avalaibles
      : Array.isArray(vehiclesList)
      ? vehiclesList
      : Object.values(vehiclesList?.avalaibles || {});
    const compatibles = Array.isArray(vehiclesList?.compatibles)
      ? vehiclesList.compatibles
      : Object.values(vehiclesList?.compatibles || {});
    const otherAxes = Array.isArray(vehiclesList?.otherAxes)
      ? vehiclesList.otherAxes
      : Object.values(vehiclesList?.otherAxes || {});

    const categorizedOptions = [
      ...avalaibles.map((v) => ({
        ...v,
        category: 'Entièrement disponibles'
      })),
      ...compatibles.map((v) => ({
        ...v,
        category: 'Sur un trajet partiellement similaire'
      })),
      ...otherAxes.map((v) => ({
        ...v,
        category: 'En mission'
      }))
    ];

    const seen = new Set();
    return categorizedOptions.filter((vehicle) => {
      const identity = obtenirIdentiteVehicule(vehicle);
      if (!identity) {
        return true;
      }
      if (seen.has(identity)) {
        return false;
      }
      seen.add(identity);
      return true;
    });
  };
  const chargerCatalogueVehicules = async (begin, end, transportId = null) => {
    if (!begin || !end) {
      return CATALOGUE_VEHICULES_VIDE;
    }
    const result = await VehicleService.getvehiclesAvalaible(
      begin,
      end,
      transportId
    );
    if (!result || result?.status === false) {
      throw new Error(result?.message || 'Chargement des véhicules impossible.');
    }
    const grouped = normaliserGroupesVehiculesParType(result?.data || {});
    return {
      fid: categoriserVehicules(grouped.fid),
      location: categoriserVehicules(grouped.location),
    };
  };

  const empreinteChargement = useMemo(
    () => JSON.stringify({
      isEdit: Boolean(isEdit),
      missionId: mission?.mission_planning_id || null,
      agendaId: mission?.mission_planning_agenda_id || null,
      captainId: mission_timesheet_user_id || mission?.mission_timesheet_user_id || null,
      missionnaries: (mission?.missionnaries || []).map((person) => person?.user_id),
      groups: groupesDestinations.map((group) => ({
        type: group?.transport_type || null,
        rentId: obtenirIdLocationDuGroupe(group),
        destinations: (group?.destinations || []).map((destination) => ({
          key: obtenirCleDestination(destination),
          begin: destination?.date_begin || destination?.date_debut || null,
          end: destination?.date_end || destination?.date_fin || null,
        })),
      })),
    }),
    [
      groupesDestinations,
      isEdit,
      mission?.mission_planning_agenda_id,
      mission?.mission_planning_id,
      mission?.mission_timesheet_user_id,
      mission?.missionnaries,
      mission_timesheet_user_id,
    ]
  );

  useEffect(() => {
    const numeroChargement = ++sequenceChargement.current;
    const chargementActif = () => sequenceChargement.current === numeroChargement;

    if (!openDialog) {
      setChargementOuvert(false);
      setCataloguesVehicules({});
      setGroupesFormulaire([]);
      setIdsTransportsPersistes([]);
      return undefined;
    }

    const initialiserFormulaire = async () => {
      setChargementOuvert(true);
      const erreurs = [];
      const missionnariesData = Array.isArray(mission?.missionnaries)
        ? mission.missionnaries
        : [];
      const fallbackCaptainId =
        mission_timesheet_user_id || mission?.mission_timesheet_user_id || 0;

      const requeteTransports = isEdit
        ? MissionTransportService.transportsByMissionPlanning(
            mission?.mission_planning_id,
            mission?.mission_planning_agenda_id
          )
        : Promise.resolve({ status: true, data: [] });
      const [driversResult, transportsResult] = await Promise.allSettled([
        VehicleService.driversByUser(),
        requeteTransports,
      ]);

      if (!chargementActif()) return;

      const driversResponse = driversResult.status === 'fulfilled'
        ? driversResult.value
        : null;
      if (
        driversResult.status === 'rejected' ||
        driversResponse?.status === false ||
        !Array.isArray(driversResponse?.data)
      ) {
        erreurs.push('Chargement des chauffeurs impossible.');
      }
      setChauffeurs(driversResponse?.data || []);
      setMissionnaries(missionnariesData);

      let transportsExistants = [];
      const transportsResponse = transportsResult.status === 'fulfilled'
        ? transportsResult.value
        : null;
      if (isEdit && !transportsResponse?.status) {
        erreurs.push(
          transportsResponse?.message || 'Chargement des transports impossible.'
        );
      } else {
        transportsExistants = (transportsResponse?.data || []).map((vehicle) => {
          const travelerIds = Array.isArray(vehicle?.travelers)
            ? vehicle.travelers.map((traveler) => traveler?.user_id).filter(Boolean)
            : Array.isArray(vehicle?.missionnaries)
            ? vehicle.missionnaries
                .map((missionnary) => missionnary?.user_id || missionnary)
                .filter(Boolean)
            : [];
          const transportType = vehicle?.mission_rent_id
            ? vehicle?.rent_type || 'LOCATION'
            : vehicle?.vhc_type || 'FID';

          return {
            ...vehicle,
            missionnaries: convertirIdsMissionnaires(
              travelerIds,
              missionnariesData
            ),
            driver: vehicle?.driver_name || null,
            driver_id: vehicle?.driver_id || null,
            captain_id: vehicle?.captain_id || 0,
            transport_id: vehicle?.transport_id || null,
            mission_rent_id: vehicle?.mission_rent_id || null,
            vhc_id: vehicle?.vhc_id || 0,
            vhc_conso_100km: vehicle?.vhc_conso_100km || 0,
            vhc_energy: vehicle?.vhc_energy || null,
            transport_type: transportType,
            transport_id_old: vehicle?.transport_id || null,
            source_transport_id: null,
            vehicle_assignment_mode: null,
          };
        });
      }

      const groupesInitiaux = appliquerChefParDefaut(
        construireGroupesFormulaire(
          groupesDestinations,
          missionnariesData,
          fallbackCaptainId,
          transportsExistants
        ),
        fallbackCaptainId
      );

      setIdsTransportsPersistes(
        transportsExistants
          .map((transport) => Number(transport.transport_id))
          .filter((transportId) => transportId > 0)
      );
      setGroupesFormulaire(groupesInitiaux);

      const catalogues = await Promise.all(
        groupesInitiaux.map(async (group, groupIndex) => {
          const { begin, end } = calculerPeriodeDestinations(group.destinations);
          const transportId = (group.transports || [])
            .map((row) => row.transport_id)
            .find(Boolean) || null;
          try {
            const catalogue = await chargerCatalogueVehicules(
              begin,
              end,
              transportId
            );
            return [groupIndex, catalogue];
          } catch (error) {
            console.error('Error fetching available vehicles:', error);
            erreurs.push(`Chargement des véhicules impossible pour le groupe ${groupIndex + 1}.`);
            return [groupIndex, CATALOGUE_VEHICULES_VIDE];
          }
        })
      );

      if (!chargementActif()) return;
      setCataloguesVehicules(Object.fromEntries(catalogues));
      if (erreurs.length) {
        setMessage(creerMessage('red', Array.from(new Set(erreurs)).join(' ')));
      }
      setChargementOuvert(false);
    };

    initialiserFormulaire().catch((error) => {
      if (!chargementActif()) return;
      console.error('Error initializing vehicle assignment:', error);
      setMessage(creerMessage('red', 'Chargement des données impossible.'));
      setChargementOuvert(false);
    });

    return () => {
      if (sequenceChargement.current === numeroChargement) {
        sequenceChargement.current += 1;
      }
    };
    // L'empreinte contient toutes les données métier qui autorisent une réhydratation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, empreinteChargement]);

  const fermerMessage = () =>
    setMessage((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    const transportId = dialogueCovoiturage?.vehicle?.transport_id;
    if (!dialogueCovoiturage.open || !transportId) {
      setMissionsCovoiturage([]);
      setChargementCovoiturage(false);
      return;
    }

    let isActive = true;
    const loadMissions = async () => {
      setChargementCovoiturage(true);
      try {
        const result = await MissionTransportService.missionsByTranport(transportId);
        if (isActive) {
          setMissionsCovoiturage(result?.data || []);
        }
      } catch (error) {
        if (isActive) {
          setMissionsCovoiturage([]);
        }
        console.error('Error fetching carpooling missions:', error);
      } finally {
        if (isActive) {
          setChargementCovoiturage(false);
        }
      }
    };

    loadMissions();

    return () => {
      isActive = false;
    };
  }, [dialogueCovoiturage.open, dialogueCovoiturage.vehicle?.transport_id]);

  const obtenirNomChef = (vehicle, fallbackCaptainId = null) => {
    const captainId = vehicle?.captain_id ?? fallbackCaptainId;
    const travelers = Array.isArray(vehicle?.travelers) ? vehicle.travelers : [];
    const captainTraveler = travelers.find(
      (traveler) => Number(traveler?.user_id) === Number(captainId)
    );
    if (captainTraveler?.name) {
      return captainTraveler.name;
    }
    const fallbackMissionnary = missionnaries.find(
      (person) => Number(person?.user_id) === Number(captainId)
    );
    if (fallbackMissionnary?.name) {
      return fallbackMissionnary.name;
    }
    return captainId ? `ID ${captainId}` : 'Non defini';
  };

  const obtenirOptionsChef = (vehicle = null, fallbackCaptainId = 0) => {
    const baseOptions = fusionnerOptionsMissionnaires(missionnaries);
    if (!vehicle?.transport_id) {
      return baseOptions;
    }

    const mergedOptions = fusionnerOptionsMissionnaires(
      baseOptions,
      Array.isArray(vehicle?.travelers) ? vehicle.travelers : []
    );
    const assignedCaptainId = vehicle?.captain_id ?? fallbackCaptainId;

    if (
      assignedCaptainId &&
      !mergedOptions.some(
        (option) => Number(option?.user_id) === Number(assignedCaptainId)
      )
    ) {
      mergedOptions.push({
        user_id: assignedCaptainId,
        name: obtenirNomChef(vehicle, assignedCaptainId)
      });
    }

    return mergedOptions;
  };

  const carpoolingDetails = useMemo(() => { // Détails à afficher dans le dialog de covoiturage, basé sur le véhicule sélectionné.
    const details = [];
    const currentMissionPlanningId = Number(mission?.mission_planning_id || 0);
    groupesFormulaire.forEach((group, groupIndex) => {
      (group.transports || []).forEach((row, rowIndex) => {
        if (!row.transport_id) {
          return;
        }
        const availableVehicles = inclureVehiculeSelectionne(obtenirVehiculesParType(
          groupIndex,
          row.transport_type || group.transport_type,
          row.mission_rent_id || group.mission_rent_id
        ), row);
        const vehicle = trouverOptionVehicule(availableVehicles, row);
        if (!vehicle?.transport_id) {
          return;
        }
        const destinations = Array.isArray(vehicle?.destinations)
          ? vehicle.destinations
          : [];
        const externalDestinations = destinations.filter((destination) => {
          const destinationMissionPlanningId = destination?.mission_planning_id;
          if (
            destinationMissionPlanningId === undefined ||
            destinationMissionPlanningId === null ||
            destinationMissionPlanningId === ''
          ) {
            return false;
          }
          if (!currentMissionPlanningId) {
            return false;
          }
          return Number(destinationMissionPlanningId) !== currentMissionPlanningId;
        });
        if (!externalDestinations.length) {
          return;
        }

        const destinationsSummary = Array.from(
          new Set(
            externalDestinations
              .map((destination) => (
                destination.lieu_summary || formaterLibelleDestination(destination)
              ))
              .filter(Boolean)
          )
        ).join(', ');

        details.push({
          key: `${groupIndex}-${rowIndex}`,
          vhc_imm: vehicle?.vhc_imm || row.vhc_id,
          transport_id: row.transport_id,
          captainName: obtenirNomChef(vehicle, row.captain_id),
          destinationsSummary
        });
      });
    });
    return details;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cataloguesVehicules, groupesFormulaire, mission?.mission_planning_id, missionnaries]);

  const confirmerCovoiturage = () => {
    if (!dialogueCovoiturage.vehicle) {
      setDialogueCovoiturage(ETAT_INITIAL_COVOITURAGE);
      return;
    }

    const currentCaptainId = obtenirIdChefMission();
    const vehicleSelection = construireSelectionVehicule(
      dialogueCovoiturage.vehicle,
      dialogueCovoiturage.mode,
      currentCaptainId
    );

    modifierLigne(
      dialogueCovoiturage.groupIndex,
      dialogueCovoiturage.rowIndex,
      'vhc_id',
      vehicleSelection
    );

    setDialogueCovoiturage(ETAT_INITIAL_COVOITURAGE);
  };

  const changerModeVehicule = (mode) => {
    setDialogueCovoiturage((previousDialog) => ({
      ...previousDialog,
      mode,
    }));
  };

  const ouvrirDialogueAffectation = (
    groupIndex,
    rowIndex,
    vehicle,
    mode = MODE_AFFECTATION_VEHICULE.CARPOOLING,
    action = 'confirm'
  ) => {
    if (!vehicle) return;
    setDialogueCovoiturage({
      open: true,
      groupIndex,
      rowIndex,
      vehicle: obtenirReferenceSelectionVehicule(vehicle),
      action,
      mode,
    });
  };

  const fermerDialogueCovoiturage = () =>
    setDialogueCovoiturage(ETAT_INITIAL_COVOITURAGE);


  // Ajoute une ligne avec seulement les destinations et missionnaires encore libres.
  const ajouterLigne = (groupIndex) => {
    const selectedUserIds = obtenirIdsMissionnairesSelectionnes(groupesFormulaire);
    const availableMissionnaries = missionnaries.filter(
      (m) => !selectedUserIds.includes(m.user_id)
    );
    const captainId = obtenirIdChefMission();

    setGroupesFormulaire((prev) => {
      const updated = [...prev];
      const targetGroup = {
        ...(updated[groupIndex] || {}),
        transports: [...(updated[groupIndex]?.transports || [])]
      };
      const groupDestinations = obtenirDestinationsNonAffectees(targetGroup, groupIndex);
      const transportType =
        groupesDestinations[groupIndex]?.transport_type || 'FID';
      const missionRentId = obtenirIdLocationDuGroupe(groupesDestinations[groupIndex]);
      const newRow = creerLigneTransport(
        captainId,
        availableMissionnaries,
        groupDestinations,
        transportType,
        missionRentId
      );
      targetGroup.transports.push(newRow);
      updated[groupIndex] = targetGroup;
      return updated;
    });
  };

  // Toute suppression passe par une confirmation; une ligne persistée est supprimée immédiatement.
  const demanderSuppressionLigne = (groupIndex, rowIndex) => {
    const row = groupesFormulaire[groupIndex]?.transports?.[rowIndex];
    if (!row) {
      return;
    }

    setDialogueSuppression({
      open: true,
      groupIndex,
      rowIndex,
      row,
      loading: false
    });
  };

  const fermerDialogueSuppression = () => {
    if (dialogueSuppression.loading) {
      return;
    }
    setDialogueSuppression(ETAT_INITIAL_SUPPRESSION);
  };

  const confirmerSuppressionLigne = async () => {
    const { groupIndex, rowIndex, row } = dialogueSuppression;
    if (!row || groupIndex === null || rowIndex === null) {
      fermerDialogueSuppression();
      return;
    }

    const transportId = Number(row.transport_id || 0);
    const isPersisted = transportId > 0 && idsTransportsPersistes.some(
      (id) => Number(id) === transportId
    );

    setDialogueSuppression((prev) => ({ ...prev, loading: true }));
    try {
      let reponseDetachement = null;
      if (isPersisted) {
        reponseDetachement = await MissionTransportService.detachTransportAssigned({
          transport_id: transportId,
          mission_planning_agenda_id: mission?.mission_planning_agenda_id || null,
          destinations: (row.destinations || []).map((destination) => ({
            mission_destination_id: destination?.mission_destination_id || null,
            mission_timesheet_destination_id:
              destination?.mission_timesheet_destination_id || null,
          })),
        });
        if (!reponseDetachement?.status) {
          throw new Error(
            reponseDetachement?.message || 'Échec du détachement du transport.'
          );
        }
      }

      setGroupesFormulaire((prev) => prev.map((group, currentGroupIndex) => ({
        ...group,
        transports: (group.transports || []).filter(
          (currentRow, currentRowIndex) => currentGroupIndex !== groupIndex ||
            currentRowIndex !== rowIndex
        )
      })));

      if (isPersisted) {
        const transportEncoreAffiche = groupesFormulaire.some(
          (group, currentGroupIndex) => (group.transports || []).some(
            (currentRow, currentRowIndex) =>
              (currentGroupIndex !== groupIndex || currentRowIndex !== rowIndex) &&
              Number(currentRow.transport_id || 0) === transportId
          )
        );
        if (reponseDetachement?.transport_deleted || !transportEncoreAffiche) {
          setIdsTransportsPersistes((prev) => prev.filter(
            (id) => Number(id) !== transportId
          ));
        }
        if (typeof onSuccessSubmit === 'function') {
          try {
            await onSuccessSubmit();
          } catch (refreshError) {
            console.error(
              'Transport deleted but parent refresh failed:',
              refreshError
            );
          }
        }
      }

      setMessage(creerMessage(
        'green',
        isPersisted ? 'Transport détaché avec succès.' : 'Ligne supprimée avec succès.'
      ));
      setDialogueSuppression(ETAT_INITIAL_SUPPRESSION);
    } catch (error) {
      setMessage(creerMessage(
        'red',
        error.message || 'Échec de la suppression du transport.'
      ));
      setDialogueSuppression((prev) => ({ ...prev, loading: false }));
    }
  };
  const fermerDialogue = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    closeDialog();
  };
  const fermerDialogueAvertissement = () =>
    setDialogueAvertissement((precedent) => ({ ...precedent, open: false }));

  // Centralise les effets métier déclenchés par chaque changement de champ.
  const modifierLigne = (groupIndex, rowIndex, field, value) => {
    setGroupesFormulaire((prev) => {
      const updatedGroups = [...prev];
      const group = { ...(updatedGroups[groupIndex] || {}) };
      const rows = [...(group.transports || [])];
      const currentRow = { ...(rows[rowIndex] || {}) };

      if (field === 'vhc_id') { // onselect voiture
        const availableVehicles = obtenirVehiculesParType(
          groupIndex,
          currentRow.transport_type || group.transport_type,
          currentRow.mission_rent_id || group.mission_rent_id
        );
        const matchedVehicle = trouverOptionVehicule(availableVehicles, value || {});
        const vehicle = fusionnerSelectionVehicule(matchedVehicle, value);
        const driverFromVehicle =
          vehicle?.driver_id != null
            ? chauffeurs.find((driver) => driver.user_id === vehicle.driver_id)
            : null;
        const currentMissionIds = new Set(
          (missionnaries || []).map((person) => Number(person?.user_id)).filter(Boolean)
        );
        const currentMissionCaptainId = obtenirIdChefMission();
        const assignmentMode = vehicle?.vehicle_assignment_mode || null;
        const transportId = resoudreIdTransportApresSelection(currentRow, vehicle);
        const reusesExistingTransport = Boolean(transportId);
        currentRow.vhc_id = vehicle?.vhc_id || 0;
        currentRow.transport_id = transportId;
        currentRow.source_transport_id = assignmentMode
          ? vehicle?.source_transport_id || null
          : null;
        currentRow.vehicle_assignment_mode = assignmentMode;
        currentRow.mission_rent_id =
          vehicle?.mission_rent_id ??
          currentRow?.mission_rent_id ??
          group?.mission_rent_id ??
          null;
        currentRow.transport_type = normaliserTypeTransport(
          vehicle?.rent_type || vehicle?.transport_type || currentRow.transport_type || group.transport_type,
          currentRow.mission_rent_id
        );
        currentRow.driver =
          vehicle?.driver_name || driverFromVehicle?.name || null;
        currentRow.driver_id = vehicle?.driver_id || null;

        if (reusesExistingTransport) {
          currentRow.captain_id = vehicle?.captain_id || currentMissionCaptainId;
        } else if (assignmentMode === MODE_AFFECTATION_VEHICULE.SEPARATE_TRIP) {
          currentRow.captain_id = vehicle?.captain_id || currentMissionCaptainId;
        } else if (!currentMissionIds.has(Number(currentRow.captain_id))) {
          currentRow.captain_id = currentMissionCaptainId;
        }
        currentRow.vhc_conso_100km = vehicle?.vhc_conso_100km || 0;
        currentRow.vhc_energy = vehicle?.vhc_energy || null;
      } else if (field === 'driver') { // onselect chauffeur
        currentRow.driver = value;
        currentRow.driver_id = value?.user_id || null;
      } else if (field === "destinations") { // onselect destinations
        const groupDestinations = Array.isArray(group?.destinations)
          ? group.destinations
          : [];
        const normalizedDestinations = dedoublonnerDestinations(
          (value || []).map((destination) => {
            const matchedDestination = groupDestinations.find(
              (item) => obtenirCleDestination(item) === obtenirCleDestination(destination)
            );
            return {
              ...(matchedDestination || destination),
              transport_type:
                destination?.transport_type ||
                matchedDestination?.transport_type ||
                currentRow.transport_type ||
                group.transport_type ||
                null
            };
          })
        );
        currentRow.destinations = normalizedDestinations;
      } else {
        currentRow[field] = value;
      }

      rows[rowIndex] = currentRow;
      updatedGroups[groupIndex] = { ...group, transports: rows };

      return updatedGroups;
    });
  };

  // Privilégie les lignes saisies, puis les destinations reçues dans les props.
  const obtenirDestinationsSelectionnees = () => {
    const destinationsFromRows = dedoublonnerDestinations(
      obtenirDestinationsDesLignes(groupesFormulaire)
    );
    if (destinationsFromRows.length) {
      return destinationsFromRows;
    }
    if (Array.isArray(selectedDestinations) && selectedDestinations.length) {
      if (selectedDestinations.some((item) => Array.isArray(item?.destinations))) {
        return selectedDestinations.flatMap((group) =>
          (group?.destinations || []).map((dest) => ({
            ...dest,
            transport_type: dest.transport_type || group.transport_type || null
          }))
        );
      }
      return selectedDestinations;
    }
    if (Array.isArray(groupesDestinations) && groupesDestinations.length) {
      return groupesDestinations.flatMap((group) =>
        (group?.destinations || []).map((dest) => ({
          ...dest,
          transport_type: dest.transport_type || group.transport_type || null
        }))
      );
    }
    return [];
  };

  // Valide le formulaire puis conserve exactement le contrat attendu par le backend.
  const enregistrer = async () => {
    try {
      setChargementOuvert(true);
      setEnregistrementEnCours(true);

      const destinationsSelectionnees = obtenirDestinationsSelectionnees();
      if (!destinationsSelectionnees.length) {
        setMessage(creerMessage('red', "Aucune destination sélectionnée pour l'assignation."));
        return;
      }

      const vehiculeFidManquant = groupesFormulaire.some((groupe) =>
        (groupe.transports || []).some((ligne) => {
          const typeTransport = ligne.transport_type || groupe.transport_type;
          return estTransportFid(
            typeTransport,
            ligne?.mission_rent_id || groupe?.mission_rent_id
          ) && !ligne.vhc_id;
        })
      );
      if (vehiculeFidManquant) {
        setMessage(creerMessage(
          'red',
          'Des voitures doivent être assignées pour les lignes FID.'
        ));
        return;
      }

      const transports = construireTransportsPayload(
        groupesFormulaire,
        mission_timesheet_user_id || mission?.mission_timesheet_user_id || null
      );

      const payload = {
        isEdit,
        action: isEdit ? 'update' : 'create',
        transports,
        mission_planning_agenda_id: mission.mission_planning_agenda_id,
      };
      const response = await MissionTransportService.saveTransportMission(payload);
      if (response?.status) {
        setMessage(creerMessage('green', 'Les données ont été enregistrées avec succès'));
        setGroupesFormulaire(
          construireGroupesFormulaire(
            groupesDestinations,
            missionnaries,
            mission_timesheet_user_id || mission?.mission_timesheet_user_id || 0
          )
        );
        if (typeof onSuccessSubmit === 'function') {
          try {
            await onSuccessSubmit();
          } catch (refreshError) {
            console.error('Transport saved but parent refresh failed:', refreshError);
            setMessage(creerMessage(
              'green',
              'Données enregistrées. Le rafraîchissement de la liste a échoué.'
            ));
          }
        }
        closeDialog();
      } else {
        setMessage(creerMessage(
          'red',
          response?.message || "Une erreur est survenue lors de l'enregistrement des données"
        ));
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessage(creerMessage(
        'red',
        "Une erreur est survenue lors de l'enregistrement des données"
      ));
    } finally {
      setChargementOuvert(false);
      setEnregistrementEnCours(false);
    }
  };
  return (
    <div>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={chargementOuvert} />
      <Message open={open} color={color} messageInfo={messageInfo} handleClose={fermerMessage} />
      <Dialog
        open={openDialog}
        onClose={fermerDialogue}
        disableEscapeKeyDown
        aria-labelledby="form-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth
        BackdropProps={{
          style: {
            backdropFilter: 'blur(3px)',
            backgroundColor: 'rgba(15, 23, 42, 0.55)'
          }
        }}
        PaperProps={{
          className: classes.dialogPaper,
          style: {
            background: '#ffffff',
            border: '1px solid #cfd9e8',
            borderRadius: 14,
            maxWidth: 1180,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          id="form-dialog-title"
          sx={{
            alignItems: 'center',
            background: 'linear-gradient(115deg, #123f76 0%, #075898 100%)',
            color: '#ffffff',
            display: 'flex',
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: 2
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'rgba(139, 188, 255, 0.35)',
              borderRadius: '50%',
              display: 'flex',
              flex: '0 0 auto',
              height: 56,
              justifyContent: 'center',
              width: 56
            }}
          >
            <DirectionsCarIcon fontSize="large" />
          </Box>
          <Box
            component="span"
            sx={{ flexGrow: 1, fontSize: { xs: 19, sm: 23 }, fontWeight: 600 }}
          >
            {isEdit
              ? "Modification d'une(des) voiture(s)"
              : "Assigner une(des) voiture(s)"}
            {mission?.reference ? ` - ${mission.reference}` : ''}
          </Box>
          <IconButton
            aria-label="Fermer"
            onClick={fermerDialogue}
            sx={{ color: '#ffffff' }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ backgroundColor: '#ffffff', px: { xs: 2, sm: 3 }, py: 2.5 }}
        >
          {openDialog && (
            <DialogContentText
              component="div"
              id="alert-dialog-description"
              className={`${classes.dialogText} ${classes.formArea}`}
            >
             
                  {groupesFormulaire.length > 0 && groupesFormulaire.map((group, idx) => (
                  <fieldset
                    className={classes.fieldset}
                    key={idx}
                    style={{
                      background: '#ffffff',
                      border: 'none',
                      borderRadius: 0,
                      boxShadow: 'none',
                      margin: 0,
                      maxWidth: 'none',
                      padding: 0,
                      width: '100%'
                    }}
                  >
                    <legend>
                      <Box
                        component="span"
                        sx={{
                          alignItems: 'center',
                          color: '#102d64',
                          display: 'flex',
                          fontSize: 17,
                          fontWeight: 600,
                          gap: 1
                        }}
                      >
                        <Box
                          sx={{
                            alignItems: 'center',
                            backgroundColor: '#e7f5ff',
                            borderRadius: '50%',
                            color: '#0767cf',
                            display: 'flex',
                            height: 42,
                            justifyContent: 'center',
                            width: 42
                          }}
                        >
                          <DirectionsCarIcon />
                        </Box>
                        {!group?.mission_rent_id && normaliserTypeTransportMinuscule(group.transport_type) === 'fid'
                          ? 'Informations de la voiture — Voiture FID'
                          : `Informations du transport - ${group.transport_type}`}
                      </Box>
                    </legend>

                    {(group.transports || []).map((row, index) => {
                      const vehiculesDisponibles = inclureVehiculeSelectionne(
                        obtenirVehiculesParType(
                          idx,
                          row.transport_type || group.transport_type,
                          row.mission_rent_id || group.mission_rent_id
                        ),
                        row
                      );
                      const destinationsSelectionnees = dedoublonnerDestinations(
                        [
                          ...(group.destinations || []).filter((destination) =>
                            (row?.destinations || []).some(
                              (selectedDestination) =>
                                obtenirCleDestination(selectedDestination) ===
                                obtenirCleDestination(destination)
                            )
                          ),
                          ...((row?.destinations || []).filter((destination) =>
                            !(group.destinations || []).some(
                              (groupDestination) =>
                                obtenirCleDestination(groupDestination) ===
                                obtenirCleDestination(destination)
                            )
                          ))
                        ]
                      );
                      const vehiculeSelectionne = trouverOptionVehicule(
                        vehiculesDisponibles,
                        obtenirReferenceSelectionVehicule(row)
                      );
                      const detailsCovoiturageLigne = carpoolingDetails.filter(
                        (detail) => detail.key === `${idx}-${index}`
                      );
                      const ligneEnCourseSeparee = estCourseSeparee(row);
                      const ligneEnCovoiturage =
                        row.vehicle_assignment_mode === MODE_AFFECTATION_VEHICULE.CARPOOLING ||
                        (!row.vehicle_assignment_mode && detailsCovoiturageLigne.length > 0);
                      const optionsChef = obtenirOptionsChef(
                        ligneEnCovoiturage ? vehiculeSelectionne : null,
                        row.captain_id
                      );
                      const captainSelectionId =
                        ligneEnCovoiturage
                          ? vehiculeSelectionne?.captain_id ||
                            row.captain_id ||
                            obtenirIdChefMission(optionsChef)
                          : row.captain_id || obtenirIdChefMission(optionsChef);
                      const chefSelectionne =
                        optionsChef.find(
                          (option) =>
                            Number(option?.user_id) === Number(captainSelectionId)
                        ) || null;
                      return (
                      <Paper
                        variant="outlined"
                        key={`${idx}-${index}`}
                        style={{
                          background: '#ffffff',
                          border: 'none',
                          borderBottom: '1px solid #dbe4ef',
                          borderRadius: 0,
                          boxShadow: 'none',
                          marginBottom: '18px',
                          marginTop: '12px',
                          padding: '0 0 22px'
                        }}
                      >
                      

                        <Grid container xs={12}  spacing={2} alignItems="center">

                          <Grid item xs={12} md={6}>
                           <FormControl fullWidth>
                            <LibelleChampVehicule icon={<DirectionsCarIcon fontSize="small" />}>
                              Voiture
                            </LibelleChampVehicule>
                          <Autocomplete
                            options={vehiculesDisponibles}
                            size="small"
                            groupBy={(option) => option.category}
                            isOptionEqualToValue={isSameVehicle}
                            getOptionLabel={obtenirLibelleVehicule}
                            getOptionKey={(option) =>
                              `${obtenirIdentiteVehicule(option) || obtenirLibelleVehicule(option)}:${option?.category || ''}`
                            }
                            onChange={(e, newValue) => {
                                const nouveauTransportId = Number(
                                  newValue?.transport_id || 0
                                );
                                const transportActuelId = Number(
                                  row?.transport_id || 0
                                );
                                if (
                                  nouveauTransportId > 0 &&
                                  nouveauTransportId !== transportActuelId
                                ) {
                                  ouvrirDialogueAffectation(
                                    idx,
                                    index,
                                    newValue
                                  );
                                } else {
                                  modifierLigne(idx, index, 'vhc_id', newValue);
                                }
                                setOptionSurvolee(null);
                                setAncreSurvol(null);
                            }}

                            value={vehiculeSelectionne}

                            renderInput={(params) => (
                                <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                label="Sélectionner une voiture"
                                />
                            )}

                            renderGroup={(params) => {
                                const groupStyles = {
                                'Entièrement disponibles': {
                                    backgroundColor: '#e8f5e9',
                                    color: '#2e7d32'
                                },
                                'Sur un trajet partiellement similaire': {
                                    backgroundColor: '#fffde7',
                                    color: '#f9a825'
                                },
                                'En mission': {
                                    backgroundColor: '#fce4ec',
                                    color: '#ad1457'
                                }
                                };

                                return (
                                <li key={params.key}>
                                    <div
                                    style={{
                                        padding: '6px 12px',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        borderBottom: '1px solid #ddd',
                                        ...groupStyles[params.group]
                                    }}
                                    >
                                    {params.group}
                                    </div>
                                    <ul style={{ padding: 0 }}>{params.children}</ul>
                                </li>
                                );
                            }}
                            renderOption={(props, option) => (
                                <li
                                {...props}
                                onMouseEnter={(e) => {
                                    if (
                                    (option.depart && option.arriver) ||
                                    (option.transport_id && option.vhc_imm)
                                    ) {
                                    setOptionSurvolee(option);
                                    setAncreSurvol(e.currentTarget);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setOptionSurvolee(null);
                                    setAncreSurvol(null);
                                }}
                                >
                                {obtenirLibelleVehicule(option)}
                                </li>
                            )}
                            />

                            {/* POPPER D’INFO AU HOVER pour l’aperçu des destinations d'une voiture en mission */}
                            <Popper
                                open={Boolean(optionSurvolee)}
                                anchorEl={ancreSurvol}
                                placement="right-start"
                                style={{
                                zIndex: 1500,
                                pointerEvents: 'none',
                                boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
                                borderRadius: '6px'
                                }}
                            >
                                {optionSurvolee && (
                                <Box
                                    sx={{
                                    bgcolor: 'background.paper',
                                    p: 2,
                                    borderRadius: 5,
                                    boxShadow: 3,
                                    minWidth: 320,
                                    maxWidth: 420,
                                    maxHeight: 280,
                                    overflowY: 'auto',
                                    border: '1px solid #e1e5ee',
                                    background:
                                      'linear-gradient(135deg, #ffffff 0%, #f4f7fb 100%)',
                                    '& .MuiListItem-root': {
                                      paddingTop: 4,
                                      paddingBottom: 4
                                    }
                                    }}
                                >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        pb: 1,
                                        mb: 1,
                                        borderBottom: '1px solid #e6e6e6',
                                        color: '#1976d2',
                                        fontWeight: 600
                                      }}
                                    >
                                      <PlaceIcon fontSize="small" />
                                      <div style={{ fontSize: '0.9rem' }}>
                                        Destinations
                                      </div>
                                    </Box>
                                    {optionSurvolee.destinations &&
                                    optionSurvolee.destinations.length > 0 && (
                                        <List dense>
                                        {optionSurvolee.destinations.map((destination, i) => {
                                          const destinationLabel = destination.lieu_summary || formaterLibelleDestination(destination);
                                          const dateLabel = formaterDatesDestination(destination);
                                          return (
                                            <ListItem key={i}>
                                              <ListItemText
                                                primary={destinationLabel || dateLabel || ''}
                                                secondary={destinationLabel && dateLabel ? dateLabel : ''}
                                                secondaryTypographyProps={{
                                                  style: {
                                                    fontSize: '0.78rem',
                                                    color: '#1976d2'
                                                  }
                                                }}
                                              />

                                            </ListItem>
                                          );
                                        })}
                                        </List>
                                    )}
                                </Box>
                                )}
                            </Popper>
                            </FormControl>

                            <Spacer y={1} />

                            <ChampSelection
                              icon={<BadgeIcon fontSize="small" />}
                              libelle="Chauffeur"
                              libelleSaisie="Sélectionner un chauffeur"
                              options={chauffeurs}
                              getOptionLabel={(option) => option.name || ''}
                              onChange={(nouvelleValeur) =>
                                modifierLigne(idx, index, 'driver', nouvelleValeur || '')
                              }
                              valeur={
                                chauffeurs.find(
                                  (chauffeur) => chauffeur.user_id === row.driver_id
                                ) || null
                              }
                              disabled={
                                ligneEnCovoiturage ||
                                !row.vhc_id ||
                                estTransportLocation(
                                  row.transport_type || group.transport_type,
                                  row.mission_rent_id || group.mission_rent_id
                                )
                              }
                            />
                            <Spacer y={1} />
                            <ChampSelection
                              icon={<SupervisorAccountIcon fontSize="small" />}
                              libelle="Chef de bord"
                              libelleSaisie="Sélectionner un chef de bord"
                              options={optionsChef}
                              valeur={chefSelectionne}
                              disabled={ligneEnCovoiturage}
                              getOptionLabel={(option) => option?.name || ''}
                              isOptionEqualToValue={(option, value) =>
                                Number(option?.user_id) === Number(value?.user_id)
                              }
                              onChange={(nouvelleValeur) =>
                                modifierLigne(idx, index, 'captain_id', nouvelleValeur?.user_id || 0)
                              }
                            />

                            <ChampSelection
                              multiple
                              icon={<GroupIcon fontSize="small" />}
                              libelle="Missionnaires"
                              libelleSaisie="Missionnaires"
                              options={missionnaries}
                              valeur={Array.isArray(row?.missionnaries) ? row.missionnaries : []}
                              getOptionLabel={(option) => option.name}
                              onChange={(nouvelleValeur) =>
                                modifierLigne(idx, index, 'missionnaries', nouvelleValeur)
                              }
                            />
                          </Grid>

                          <Grid container item xs={12} md={6} style={{ alignSelf: 'flex-start' }}>
                            <Grid item xs={12} display="flex" justifyContent="flex-end">
                              <IconButton
                          onClick={() => demanderSuppressionLigne(idx, index)}
                          aria-label="Supprimer le véhicule"
                          // style={{ top: "-6vw", left: '25vw' }}
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                            </Grid>
                            
                            {ligneEnCovoiturage && (
                              <InformationsCovoiturage
                                details={detailsCovoiturageLigne}
                                modifiable={Boolean(vehiculeSelectionne?.transport_id)}
                                onVoir={() => ouvrirDialogueAffectation(
                                  idx,
                                  index,
                                  vehiculeSelectionne,
                                  MODE_AFFECTATION_VEHICULE.CARPOOLING,
                                  'info'
                                )}
                                onModifier={() => ouvrirDialogueAffectation(
                                  idx,
                                  index,
                                  vehiculeSelectionne,
                                  MODE_AFFECTATION_VEHICULE.CARPOOLING
                                )}
                              />
                            )}
                            {ligneEnCourseSeparee && vehiculeSelectionne && (
                              <InformationCourseSeparee
                                modifiable={Boolean(vehiculeSelectionne.transport_id)}
                                onModifier={() => ouvrirDialogueAffectation(
                                  idx,
                                  index,
                                  vehiculeSelectionne,
                                  MODE_AFFECTATION_VEHICULE.SEPARATE_TRIP
                                )}
                              />
                            )}
                            <ChampSelection
                              multiple
                              icon={<EventIcon fontSize="small" />}
                              libelle="Dates de la mission"
                              libelleSaisie="Date(s)"
                              options={group.destinations || []}
                              valeur={destinationsSelectionnees}
                              getOptionLabel={formaterDatesDestination}
                              isOptionEqualToValue={(option, value) =>
                                obtenirCleDestination(option) === obtenirCleDestination(value)
                              }
                              onChange={(nouvelleValeur) =>
                                modifierLigne(
                                  idx,
                                  index,
                                  'destinations',
                                  dedoublonnerDestinations(nouvelleValeur || [])
                                )
                              }
                            />
                          </Grid>
                        </Grid>
                       
                      </Paper>
                      );
                    })}

                    <Spacer y={1} />
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => ajouterLigne(idx)}
                      color="info"
                      variant="outlined"
                      disabled={ajoutVehiculeDesactive(group, idx)}
                      size="medium"
                      sx={{
                        borderColor: '#84bfff',
                        borderRadius: 1.5,
                        color: '#2866ad',
                        fontWeight: 500,
                        px: 2.5,
                        py: 1,
                        textTransform: 'uppercase'
                      }}
                    >
                      Ajouter une voiture
                    </Button>
                  </fieldset>
                    ))}
              <Spacer y={1} />
            </DialogContentText>
          )}
          <Spacer y={1} />
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid #dbe4ef',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1.5,
            justifyContent: 'flex-end',
            px: { xs: 2, sm: 3 },
            py: 2
          }}
        >
          <Button
            onClick={fermerDialogue}
            variant="outlined"
            color="error"
            size="medium"
            className={classes.actionButton}
            sx={{ ...STYLE_BOUTON_ACTION, minWidth: 145 }}
          >
            Annuler
          </Button>
          <LoadingButton
            onClick={enregistrer}
            color="info"
            variant="contained"
            size="medium"
            disabled={enregistrementEnCours}
            startIcon={<SaveIcon />}
            className={classes.actionButton}
            sx={{ ...STYLE_BOUTON_ACTION, minWidth: 190 }}
          >
            Enregistrer
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <DialogueSuppressionVehicule
        dialogue={dialogueSuppression}
        idsPersistes={idsTransportsPersistes}
        classes={classes}
        onFermer={fermerDialogueSuppression}
        onConfirmer={confirmerSuppressionLigne}
      />
      {/* Dialogue de confirmation de covoiturage. */}
      <CarPoolingConfirm
        carpoolingDialog={dialogueCovoiturage}
        carpoolingMissions={missionsCovoiturage}
        carpoolingLoading={chargementCovoiturage}
        handleCloseCarpoolingDialog={fermerDialogueCovoiturage}
        handleConfirmCarpooling={confirmerCovoiturage}
        handleChangeVehicleMode={changerModeVehicule}
        getCaptainName={obtenirNomChef}
        classes={classes}
      />
      <DialogueAvertissementKilometrage
        dialogue={dialogueAvertissement}
        classes={classes}
        onFermer={fermerDialogueAvertissement}
      />
    </div>
  );
};

export default MissionAssignVehicle;
